// App update lifecycle: version polling + service-worker handoff + safe refresh/repair.
// Offline data (IndexedDB "buildflow-offline", queued photos/uploads) is NEVER touched here.
import { BUILD_STAMP } from "@/buildStamp";

const VERSION_URL = "/version.json";
const POLL_MS = 5 * 60 * 1000;

let latest = null;
let waitingWorker = null;
let registration = null;
let refreshing = false;
let captureActive = false; // camera open / walkthrough recording — updates must wait (Group A4)
const listeners = new Set();

export function isCaptureActive() { return captureActive; }
export function setCaptureActive(v) {
  captureActive = !!v;
  try { window.dispatchEvent(new CustomEvent("bf:capture-state", { detail: { active: captureActive } })); } catch (e) { /* non-browser env */ }
}

async function fetchVersion() {
  try {
    const r = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: "no-store" });
    if (r.ok) return await r.json();
  } catch (e) { /* offline */ }
  return null;
}

export function getState() {
  return {
    // Round 17 #5: "current" is the RUNNING bundle's build, baked in at build
    // time — never a network fetch (that always equals the server and made the
    // banner fire off a stuck waiting worker instead of a real version gap).
    current: BUILD_STAMP,
    latest,
    updateAvailable: !!(latest && latest.build && latest.build !== BUILD_STAMP),
    online: typeof navigator === "undefined" ? true : navigator.onLine,
    swReady: !!registration,
  };
}

function emit() {
  const s = getState();
  listeners.forEach((cb) => { try { cb(s); } catch (e) { /* ignore */ } });
}

export function subscribe(cb) {
  listeners.add(cb);
  cb(getState());
  return () => listeners.delete(cb);
}

// One-time defensive purge on app load: drop any generated-report PDFs an old
// service worker or cache layer stored — reports must always render fresh.
async function purgeStaleReportCaches() {
  try {
    if (!("caches" in window)) return;
    const keys = await caches.keys();
    for (const k of keys) {
      const c = await caches.open(k);
      const reqs = await c.keys();
      await Promise.all(reqs.filter((r) => {
        try {
          const p = new URL(r.url).pathname;
          return p.startsWith("/api/reports/") || p.startsWith("/api/public/report/");
        } catch { return false; }
      }).map((r) => c.delete(r)));
    }
  } catch (e) { /* ignore */ }
}

export function initAppUpdates() {
  purgeStaleReportCaches();
  fetchVersion().then((v) => { latest = v; emit(); });
  setInterval(() => { checkForUpdates(); }, POLL_MS);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      checkForUpdates();
      try { registration?.update?.(); } catch { /* ignore */ }
      return;
    }
  });
  // STOP-SHIP: an update must never wait on a non-empty upload queue again —
  // that rule deadlocked a broken old client out of the fixed build (its
  // queue never drained, so it never updated). The queue lives in IndexedDB
  // and survives the reload. Only an OPEN camera still holds updates.
  const maybeApply = () => {
    if (!waitingWorker || refreshing || captureActive) return;
    setTimeout(() => { if (waitingWorker && !refreshing && !captureActive) applyUpdate(); }, 1500);
  };
  window.addEventListener("bf:capture-state", (e) => { if (!e?.detail?.active) maybeApply(); });
  window.addEventListener("online", emit);
  window.addEventListener("offline", emit);
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", async () => {
    try {
      registration = await navigator.serviceWorker.register("/sw.js");
      registration.addEventListener("updatefound", () => {
        const w = registration.installing;
        if (!w) return;
        w.addEventListener("statechange", () => {
          if (w.state === "installed" && navigator.serviceWorker.controller) {
            waitingWorker = registration.waiting || w;
            emit();
            maybeApply(); // auto-apply unless the camera is open
          }
        });
      });
      if (registration.waiting && navigator.serviceWorker.controller) {
        waitingWorker = registration.waiting;
        emit();
        maybeApply(); // an update was already waiting when the app booted
      }
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return; // reload exactly once — no loops
        refreshing = true;
        window.location.reload();
      });
    } catch (e) { /* registration failed */ }
  });
}

export async function checkForUpdates() {
  const v = await fetchVersion();
  if (v) latest = v;
  try { if (registration) await registration.update(); } catch (e) { /* ignore */ }
  emit();
  return getState();
}

export function applyUpdate() {
  if (waitingWorker) {
    waitingWorker.postMessage({ type: "SKIP_WAITING" }); // controllerchange -> single reload
  } else {
    window.location.reload();
  }
}

// Safe refresh: re-check version, reload latest files. Keeps projects, photos, drafts, account.
export async function refreshApplication() {
  await checkForUpdates();
  window.location.reload();
}

// Repair: sign out, purge obsolete app caches, re-register SW on next load, keep offline queue.
export async function repairApp() {
  try {
    const keys = await caches.keys();
    await Promise.all(keys.filter((k) => k.startsWith("bp360-") || k.startsWith("buildflow-"))
      .map((k) => caches.delete(k)));
  } catch (e) { /* ignore */ }
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  } catch (e) { /* ignore */ }
  try {
    localStorage.removeItem("buildflow.token"); // sign out; offline IndexedDB queue is preserved
    sessionStorage.clear();
  } catch (e) { /* ignore */ }
  window.location.href = "/login";
}

// Clear app caches and reload — keeps login token, language AND offline queue (IndexedDB).
export async function clearAppCache() {
  try {
    const keys = await caches.keys();
    await Promise.all(keys.filter((k) => k.startsWith("bp360-") || k.startsWith("buildflow-"))
      .map((k) => caches.delete(k)));
  } catch (e) { /* ignore */ }
  try { if (registration) await registration.update(); } catch (e) { /* ignore */ }
  window.location.reload();
}

export async function pendingUploads() {
  try {
    return await new Promise((resolve) => {
      const req = indexedDB.open("buildflow-offline");
      req.onsuccess = () => {
        try {
          const db = req.result;
          const names = Array.from(db.objectStoreNames || []);
          if (!names.length) { resolve(0); return; }
          let total = 0;
          let done = 0;
          names.forEach((n) => {
            try {
              // P0-5/P0-7: only work that still needs the network blocks an
              // update — uploaded confirmations and orphaned photos don't.
              const c = db.transaction(n, "readonly").objectStore(n).getAll();
              c.onsuccess = () => { total += (c.result || []).filter((i) => i.status !== "uploaded" && i.status !== "orphaned").length; done += 1; if (done === names.length) resolve(total); };
              c.onerror = () => { done += 1; if (done === names.length) resolve(total); };
            } catch (e) { done += 1; if (done === names.length) resolve(total); }
          });
        } catch (e) { resolve(0); }
      };
      req.onerror = () => resolve(0);
    });
  } catch (e) { return 0; }
}
